<script lang="ts" setup>
// @ts-ignore
// @ts-ignore
import { TrackerDataType } from '@scr-npm/nuxt-tracker/dist/runtime/types'
import VueEasyLightbox, {
    useEasyLightbox,
    UseEasyLightboxOptions,
} from 'vue-easy-lightbox'
import { SrcSetSizes } from '~/composables/useSrcSet'

export interface GalleryProps {
    data: Gallery
    thumbnail: ThumborThumbnail
    headingLevel: HeadingLevel
    extra?: string
}

const props = defineProps<GalleryProps>()
const { $t } = useNuxtApp()

const images = computed((): Pick<UseEasyLightboxOptions, 'imgs'> => {
    return {
        imgs: props.data.data.map((image): any => {
            return {
                src: image.file?.path?.['1170x0-t']?.url ?? '',
                title: image.title || '',
                alt: image.alt ||image.title || '',
            }
        }),
    }
})
const thumbnailSrc = computed((): SrcSetSizes | undefined =>
    useSrcSetSizes(props.thumbnail)
)
const { show, visibleRef, onHide } = useEasyLightbox({
    ...images.value,
})

// Focus trap for lightbox (WCAG 2.4.3 compliance)
const lightboxWrapperRef = ref<HTMLElement>()
const triggerButtonRef = ref<HTMLElement>()

// Screen reader announcement for gallery state (WCAG 4.1.2)
const galleryAnnouncement = ref<string>('')
const focusTrap = useFocusTrap(lightboxWrapperRef, {
    returnFocusOnDeactivate: true,
    onEscape: () => {
        onHide()
    },
})

// Open lightbox and store trigger element for focus return
const openLightbox = (): void => {
    triggerButtonRef.value = document.activeElement as HTMLElement
    show()
}

// Activate/deactivate focus trap when lightbox visibility changes
watch(visibleRef, (isVisible) => {
    if (isVisible) {
        // WCAG 2.4.3: Aggressively move focus to lightbox
        // VueEasyLightbox renders asynchronously, so we need multiple attempts
        activateLightboxFocus()

        // Announce gallery opened (WCAG 4.1.2)
        galleryAnnouncement.value = ''
        setTimeout(() => {
            const imageCount = props.data.data.length
            galleryAnnouncement.value = `${$t('Galéria otvorená.')} ${imageCount} ${$t('obrázkov.')} ${$t('Použite šípky pre navigáciu, Escape pre zatvorenie.')}`
        }, 100)
    } else {
        focusTrap.deactivate()

        // Announce gallery closed (WCAG 4.1.2)
        galleryAnnouncement.value = ''
        setTimeout(() => {
            galleryAnnouncement.value = $t('Galéria zatvorená.') as string
        }, 100)

        // Return focus to trigger button
        if (triggerButtonRef.value) {
            nextTick(() => {
                triggerButtonRef.value?.focus()
            })
        }
    }
})

/**
 * WCAG 2.4.3: Aggressively activate focus on the lightbox
 * Uses multiple attempts because VueEasyLightbox renders asynchronously
 */
const activateLightboxFocus = (attempt: number = 0): void => {
    if (attempt > 20) return // Max 20 attempts (4 seconds total)

    nextTick(() => {
        if (!lightboxWrapperRef.value) {
            // Wrapper not ready yet, retry
            setTimeout(() => activateLightboxFocus(attempt + 1), 200)
            return
        }

        // First, ensure the wrapper itself is focusable and focus it immediately
        // This ensures focus moves away from the trigger button right away
        if (attempt === 0) {
            lightboxWrapperRef.value.setAttribute('tabindex', '-1')
            lightboxWrapperRef.value.focus()
        }

        // Try to find and focus the close button
        const closeBtn = lightboxWrapperRef.value.querySelector<HTMLElement>(
            '.btn__close, .vel-btns-wrapper button, [class*="btn"]'
        )

        if (closeBtn) {
            focusTrap.activate()
            closeBtn.focus()
            enhanceLightboxAccessibility()
        } else {
            // Button not rendered yet, retry
            setTimeout(() => activateLightboxFocus(attempt + 1), 200)
        }
    })
}

// Also watch for ref assignment to handle edge cases
watch(lightboxWrapperRef, (newRef) => {
    if (newRef && visibleRef.value && !focusTrap.isActive.value) {
        // Use the same aggressive focus approach
        activateLightboxFocus()
    }
})

// vue-easy-lightbox renders controls as <div role="button"> without tabindex,
// so keyboard users can't tab to them and the focus trap skips them.
const activateDivButton = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).click()
}

/**
 * Enhance lightbox accessibility by adding Slovak aria-labels to buttons
 * Addresses WCAG 2.4.6 (Headings and Labels) - buttons should have labels in page language
 */
const enhanceLightboxAccessibility = (): void => {
    if (!lightboxWrapperRef.value) return

    const buttonLabels: Record<string, string> = {
        '.btn__close': $t('Zavrieť galériu') as string,
        '.btn__prev': $t('Predchádzajúci obrázok') as string,
        '.btn__next': $t('Nasledujúci obrázok') as string,
        '.toolbar-btn__zoomin': $t('Priblížiť') as string,
        '.toolbar-btn__zoomout': $t('Oddialiť') as string,
        '.toolbar-btn__resize': $t('Obnoviť veľkosť') as string,
        '.toolbar-btn__rotate': $t('Otočiť') as string,
    }

    Object.entries(buttonLabels).forEach(([selector, label]) => {
        const btns = lightboxWrapperRef.value?.querySelectorAll<HTMLElement>(selector)
        if (!btns?.length) return
        btns.forEach((b) => {
            b.setAttribute('aria-label', label)
            // Make the div focusable unless it's disabled
            if (b.classList.contains('disable')) {
                b.setAttribute('tabindex', '-1')
                b.setAttribute('aria-disabled', 'true')
            } else {
                b.setAttribute('tabindex', '0')
                b.removeAttribute('aria-disabled')
            }
            b.removeEventListener('keydown', activateDivButton)
            b.addEventListener('keydown', activateDivButton)
        })
    })

    // Add aria-live region for image change announcements (WCAG 4.1.3)
    const imgTitle = lightboxWrapperRef.value.querySelector('.vel-img-title')
    if (imgTitle && !imgTitle.getAttribute('aria-live')) {
        imgTitle.setAttribute('aria-live', 'polite')
        imgTitle.setAttribute('aria-atomic', 'true')
    }
}
</script>

<template>
  <section>
    <Tracker :data="{ position: props.data.title, title: props.extra } as TrackerDataType">
      <BoxHeader
        :heading="props.data.title"
        :subtitle="props.data.subtitle"
        :heading-level="props.headingLevel"
      />
      <!-- WCAG 1.1.1: aria-describedby links image to its caption for screen readers -->
      <!-- WCAG 2.4.3: Focus order - image, then caption, then gallery button -->
      <figure
        data-cy="gallery_box"
        class="gallery-figure"
      >
        <div class="gallery-image-wrapper position-relative">
          <div
            v-if="thumbnailSrc?.srcSet"
            class="ratio ratio-16x9 rounded overflow-hidden"
          >
            <img
              :srcset="thumbnailSrc?.srcSet"
              sizes="(min-width: 1170px) 1170px, 100vw"
              loading="lazy"
              class="w-100 h-100 d-block object-fit-cover"
              decoding="async"
              :alt="props.extra"
              :aria-describedby="props.extra ? 'gallery-caption' : undefined"
              width="1170"
              height="700"
            >
          </div>
        </div>
        <!-- Caption comes before button in DOM for correct screen reader order -->
        <figcaption
          v-if="props.extra"
          id="gallery-caption"
          class="gallery-figcaption fs-smaller text-center bg-gray-3 rounded-bottom px-3 pb-3"
        >
          {{ props.extra }}
        </figcaption>
        <!-- Button positioned absolutely over the image, but after caption in DOM (WCAG 2.4.3) -->
        <!-- Visible text "X fotiek v galérii" is the accessible name - no aria-label needed -->
        <VButton
          class="gallery-button position-absolute end-0 me-3 mb-3 mb-md-4 me-md-4"
          color="primary"
          size="large"
          type="button"
          @click="openLightbox"
        >
          {{ props.data.data.length }}
          <span class="fw-normal text-uppercase fs-small ms-1">
            {{
              usePlural(
                ['fotka', 'fotky', 'fotiek'],
                props.data.data.length
              )
            }}
            v {{ $t('galérii') }}
          </span>
          <svg
            aria-hidden="true"
            class="d-block ms-2 text-white"
            width="6"
            height="14"
          >
            <use xlink:href="#angle-right" />
          </svg>
        </VButton>
      </figure>
      <Teleport to="#modal-wrap">
        <div
          v-if="visibleRef"
          ref="lightboxWrapperRef"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('Galéria obrázkov')"
        >
          <VueEasyLightbox
            :loop="true"
            :visible="visibleRef"
            :imgs="images.imgs"
            :move-disabled="true"
            @hide="onHide"
          />
        </div>
      </Teleport>
      <!-- Screen reader announcement for gallery state (WCAG 4.1.2) -->
      <Teleport to="body">
        <div
          aria-live="assertive"
          aria-atomic="true"
          class="visually-hidden"
        >
          {{ galleryAnnouncement }}
        </div>
      </Teleport>
    </Tracker>
  </section>
</template>
<style lang="scss" scoped>
@import './Gallery.scss';
</style>
